import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Briefcase, Clock, CheckCircle, Plus, LogOut, Key, UserPlus, FileText, Building2, Mail, UserCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAutoLogout } from '../hooks/useAutoLogout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useAutoLogout(); // Enable auto-logout after 5 minutes of inactivity
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (err) {
      setError('Error al cargar estadísticas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="/surnexa-logo.png" 
                alt="Surnexa Logo" 
                className="w-32 h-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">
                  Panel de Administrador
                </h1>
                <p className="text-gray-600 mt-1">Bienvenido, {user?.nombre_completo}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/mi-perfil')}
                data-testid="my-profile-button"
              >
                <UserCircle className="mr-2 h-4 w-4" />
                Mi Perfil
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/cambiar-contrasena')}
                data-testid="change-password-button"
              >
                <Key className="mr-2 h-4 w-4" />
                Cambiar Clave
              </Button>
              <Button variant="outline" onClick={logout} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                Salir
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/crear-trabajo')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 p-3 rounded-full">
                  <Briefcase className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Crear Trabajo</p>
                  <p className="text-lg font-semibold">Nuevo</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/crear-cliente')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-teal-100 p-3 rounded-full">
                  <UserPlus className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Crear Cliente</p>
                  <p className="text-lg font-semibold">Nuevo</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/crear-tecnico')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <UserPlus className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Crear Técnico</p>
                  <p className="text-lg font-semibold">Nuevo Usuario</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/trabajos')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-orange-100 p-3 rounded-full">
                  <Briefcase className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Gestionar Trabajos</p>
                  <p className="text-lg font-semibold">Asignar y Ver</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/informes')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-indigo-100 p-3 rounded-full">
                  <FileText className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Crear Informes</p>
                  <p className="text-lg font-semibold">Reportes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-purple-200" onClick={() => navigate('/admin/configuracion-smtp')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-purple-100 p-3 rounded-full">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Configurar Email</p>
                  <p className="text-lg font-semibold">SMTP / 2FA</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Cargando estadísticas...</p>
            </CardContent>
          </Card>
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : stats ? (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Estadísticas Generales</h2>
            
            {/* Users Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card 
                className="bg-blue-50 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/reporte/usuarios')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-blue-800 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Total Usuarios
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-900">{stats.total_usuarios}</div>
                </CardContent>
              </Card>
              
              <Card 
                className="bg-green-50 border-green-200 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/tecnicos')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-green-800">Técnicos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-900">{stats.total_tecnicos}</div>
                </CardContent>
              </Card>
              
              <Card 
                className="bg-purple-50 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/clientes-empresa')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-purple-800">Usuarios de Cliente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-900">{stats.total_clientes}</div>
                </CardContent>
              </Card>
            </div>

            {/* Work Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card 
                className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/reporte/trabajos')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-800 flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Total Trabajos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{stats.total_trabajos}</div>
                </CardContent>
              </Card>

              <Card 
                className="bg-yellow-50 border-yellow-200 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/reporte/trabajos?estado=pendiente')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-yellow-800 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Pendientes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-yellow-900">{stats.trabajos_pendientes}</div>
                </CardContent>
              </Card>
              
              <Card 
                className="bg-blue-50 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/reporte/trabajos?estado=en_progreso')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-blue-800">En Progreso</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-900">{stats.trabajos_en_progreso}</div>
                </CardContent>
              </Card>
              
              <Card 
                className="bg-green-50 border-green-200 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/reporte/trabajos?estado=completado')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-green-800 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Completados
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-900">{stats.trabajos_completados}</div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AdminDashboard;